'use client'

import { memo, useState, useCallback, useRef, type ComponentType } from 'react'
import { useRouter } from 'next/navigation'
import type { HeroDemo } from '@kiltechco/dday-templates/demos'

interface TemplateShowcaseProps {
  className?: string
  children: React.ReactNode
}

interface DesignPreviewContainerProps {
  isOpen: boolean
  onClose: () => void
  design: HeroDemo | null
  onSelect: (design: HeroDemo) => void
}

function reportShowcaseLoadError(error: unknown) {
  // Keep Sentry/logger code out of the homepage critical bundle.
  void import('@/lib/logger')
    .then(({ logger }) => {
      logger.error('Failed to load design:', error)
    })
    .catch(() => {
      console.error('Failed to load design:', error)
    })
}

export default memo(function TemplateShowcase({
  className = '',
  children,
}: TemplateShowcaseProps) {
  const router = useRouter()
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [previewDesign, setPreviewDesign] = useState<HeroDemo | null>(null)
  const [isLoadingDesign, setIsLoadingDesign] = useState(false)
  const [DesignPreviewContainer, setDesignPreviewContainer] =
    useState<ComponentType<DesignPreviewContainerProps> | null>(null)
  const loadingRef = useRef(false)
  const hoveredCardRef = useRef<HTMLElement | null>(null)

  const handleDesignClick = useCallback(
    async (designId: string, designLabel: string) => {
      if (loadingRef.current) return
      loadingRef.current = true
      setIsLoadingDesign(true)

      try {
        // The homepage cards stay server-rendered. We only load the full demo map
        // and preview drawer when a visitor actually interacts with a card.
        const [{ HERO_DEMOS }, previewModule] = await Promise.all([
          import('@kiltechco/dday-templates/demos'),
          DesignPreviewContainer
            ? Promise.resolve(null)
            : import('@/app/designs/_components/DesignPreviewContainer'),
        ])

        const fullDesign = HERO_DEMOS[designId]
        if (fullDesign) {
          if (previewModule?.default) {
            setDesignPreviewContainer(() => previewModule.default)
          }
          void import('@/lib/analytics').then(({ analytics }) => {
            analytics.page.designPreviewed(designId, designLabel)
          })
          setPreviewDesign(fullDesign)
          setIsPreviewOpen(true)
        } else {
          window.open(
            `/designs/demo/${designId}`,
            '_blank',
            'noopener,noreferrer'
          )
        }
      } catch (error) {
        reportShowcaseLoadError(error)
        window.open(
          `/designs/demo/${designId}`,
          '_blank',
          'noopener,noreferrer'
        )
      } finally {
        setIsLoadingDesign(false)
        loadingRef.current = false
      }
    },
    [DesignPreviewContainer]
  )

  const handleDesignSelect = useCallback(
    (design: HeroDemo) => {
      void import('@/lib/analytics').then(({ analytics }) => {
        analytics.builder.templateSelected(design.id, design.label)
      })
      router.push(`/builder?heroId=${design.id}`)
    },
    [router]
  )

  // Cards stay server-rendered; delegated handlers read payload from data attributes.
  const getCardFromEvent = (target: EventTarget): HTMLElement | null =>
    (target as HTMLElement).closest<HTMLElement>('[data-design-id]')

  const handleContainerClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isLoadingDesign) return
      const card = getCardFromEvent(e.target)
      if (!card) return
      const { designId, designLabel } = card.dataset
      if (designId && designLabel) handleDesignClick(designId, designLabel)
    },
    [isLoadingDesign, handleDesignClick]
  )

  const handleContainerKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Enter' && e.key !== ' ') return
      if (isLoadingDesign) return
      const card = getCardFromEvent(e.target)
      if (!card) return
      const { designId, designLabel } = card.dataset
      if (designId && designLabel) {
        e.preventDefault()
        handleDesignClick(designId, designLabel)
      }
    },
    [isLoadingDesign, handleDesignClick]
  )

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const card = getCardFromEvent(e.target)
      if (!card) return
      if (hoveredCardRef.current === card) return

      if (hoveredCardRef.current) {
        const prevInner = hoveredCardRef.current
          .firstElementChild as HTMLElement | null
        if (prevInner) {
          prevInner.style.transform =
            'rotateY(0deg) rotateX(0deg) translateZ(0px) scale(1)'
          prevInner.style.boxShadow = ''
        }
      }

      // Apply the 3D tilt to the inner child, NOT the card itself.
      // The card element is the scroll-snap item inside overflow-x-auto — if we
      // transform it directly, the browser routes vertical scroll events to the
      // overflow container instead of the page. Transforming the inner child keeps
      // the card in its normal layout space so scroll routing stays correct.
      const inner = card.firstElementChild as HTMLElement | null
      if (!inner) return

      const index = Array.from(card.parentElement?.children ?? []).indexOf(card)
      const isEven = index % 2 === 0
      inner.style.transition =
        'transform 0.3s ease-out, box-shadow 0.3s ease-out'
      inner.style.transformStyle = 'preserve-3d'
      inner.style.transform = `rotateY(${isEven ? '8deg' : '-8deg'}) rotateX(-3deg) translateZ(20px) scale(1.05)`
      inner.style.boxShadow = '0 25px 50px -12px rgba(0,0,0,0.25)'
      hoveredCardRef.current = card
    },
    []
  )

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const card = getCardFromEvent(e.target)
      if (!card) return

      const relatedTarget = e.relatedTarget as HTMLElement | null
      if (relatedTarget && card.contains(relatedTarget)) return

      const inner = card.firstElementChild as HTMLElement | null
      if (inner) {
        inner.style.transform =
          'rotateY(0deg) rotateX(0deg) translateZ(0px) scale(1)'
        inner.style.boxShadow = ''
      }
      if (hoveredCardRef.current === card) {
        hoveredCardRef.current = null
      }
    },
    []
  )

  return (
    <>
      <div className={`w-full ${className}`}>
        <div className="relative">
          <div
            aria-hidden
            className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 sm:w-16 z-10 bg-linear-to-r from-background to-transparent"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 sm:w-16 z-10 bg-linear-to-l from-background to-transparent"
          />

          <div className="overflow-x-auto snap-x snap-mandatory py-2 sm:py-4 select-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {/* The cards carry semantics; this wrapper only handles delegation and tilt. */}
            <div
              role="presentation"
              onClick={handleContainerClick}
              onKeyDown={handleContainerKeyDown}
              onMouseOver={handleMouseEnter}
              onMouseOut={handleMouseLeave}
              className={
                isLoadingDesign ? 'opacity-70 pointer-events-none' : undefined
              }
            >
              {children}
            </div>
          </div>
        </div>
      </div>

      {DesignPreviewContainer && isPreviewOpen && previewDesign && (
        <DesignPreviewContainer
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          design={previewDesign}
          onSelect={handleDesignSelect}
        />
      )}
    </>
  )
})
